import { useEffect, useState, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import "@/App.css";
import { toast } from 'sonner';
import { budgetPeriodRange, dateRangeKey } from './lib/dateRanges';
import { Toaster } from './components/ui/sonner';
import { Sidebar, MobileNav, ProfileMenu } from './components/Sidebar';
import { ProfileMenuContext } from './context/ProfileMenuContext';
import { LocalTransactionsContext } from './context/LocalTransactionsContext';
import AcquisitionSourceModal from './components/AcquisitionSourceModal';
import FeedbackModal from './components/FeedbackModal';
import GmailAccessGrantedModal from './components/GmailAccessGrantedModal';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Categories from './pages/Categories';
import Transactions from './pages/Transactions';
import Charts from './pages/Charts';
import Settings from './pages/Settings';
import Investments from './pages/Investments';
import Goals from './pages/Goals';
import GoalInviteModal from './components/GoalInviteModal';
import GoogleConsentNotice from './components/GoogleConsentNotice';
import * as api from './lib/api';
import { getToken } from './lib/api';
import { syncPushOnLoad } from './lib/push';
import { captureUtmParams, captureRefParam, captureGoalInvite, getPendingGoalInvite, clearPendingGoalInvite } from './lib/attribution';
import { AmountsVisibilityProvider } from './context/AmountsVisibilityContext';
import { PaywallProvider, usePaywall } from './context/PaywallContext';
import { useTheme } from './context/ThemeContext';
import PaywallModal from './components/PaywallModal';
import AdminApp from './pages/admin/AdminApp';
import { friendlyError } from './lib/errorReporting';

// Lazy: react-markdown (Coach replies) is ~38 kB gzip that only the Coach needs.
const Chat = lazy(() => import('./pages/Chat'));

// Feedback prompt waits this long after onboarding (profile.created_at)
// before it's eligible to show — rating the app the moment you finish
// onboarding, before you've actually used it, doesn't produce useful
// feedback. Tune freely; not a server-enforced boundary, just display timing.
const FEEDBACK_MIN_DAYS_SINCE_ONBOARDING = 3;

function AppShell() {
  const { openPaywall } = usePaywall();
  const { setPreference: setThemePreference } = useTheme();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [pushState, setPushState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    // Deep-link support (no router): OAuth callback lands on /settings?gmail=…,
    // push notifications open the app with ?page=….
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const VALID = ['dashboard', 'transactions', 'categories', 'goals', 'charts', 'investments', 'chat', 'settings'];
    const pageParam = params.get('page');
    if (pageParam && VALID.includes(pageParam)) return pageParam;
    if (path.startsWith('/settings') || params.get('gmail')) return 'settings';
    return 'dashboard';
  });
  const [dashboard, setDashboard] = useState(null);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [goals, setGoals] = useState([]);
  // Metas detail open inside the Goals page (null = list). Lives here so the
  // Dashboard's goal bars can open a specific goal directly.
  // Seeded from ?goal= when a goal notification opened the app fresh.
  // Bumped whenever data is refreshed from outside the current screen
  // (notification tap, app back in the foreground) so an already-open goal
  // detail refetches too, not just App-level lists.
  const [refreshNonce, setRefreshNonce] = useState(0);
  // Set once signed in + onboarded (see the effect after fetchAll); no-op before.
  const refreshRef = useRef(() => {});
  const [openGoalId, setOpenGoalId] = useState(() => new URLSearchParams(window.location.search).get('goal'));
  // Shared-goal invite from a ?goal_invite= link, held until signed in +
  // onboarded (see lib/attribution.js).
  const [pendingGoalInvite, setPendingGoalInvite] = useState(() => {
    captureGoalInvite();
    return getPendingGoalInvite();
  });
  // Gmail integration status — fetched app-wide (not just in Settings) so the
  // Dashboard can show its own small connect CTA / reauth nudge.
  const [gmailIntegration, setGmailIntegration] = useState(null);
  const [gmailOAuthEnabled, setGmailOAuthEnabled] = useState(true);
  // See GOOGLE_OAUTH_VERIFIED in server.py — false while Google's consent
  // screen is still in Testing mode, so Dashboard/Settings offer a "request
  // access" capture instead of the real (broken-for-most-users) connect flow.
  const [gmailOAuthVerified, setGmailOAuthVerified] = useState(true);
  const [gmailAccessRequestedAt, setGmailAccessRequestedAt] = useState(null);
  // Gate the Dashboard CTA render on this instead of assuming "not connected"
  // until the fetch resolves — avoids a flash-in-then-vanish of that element
  // on every load (worse: right next to the onboarding tour's first target,
  // see Dashboard.js/gmailLoaded).
  const [gmailLoaded, setGmailLoaded] = useState(false);
  const [dateRange, setDateRange] = useState();
  // False until the global date filter has been defaulted to the budget
  // period (see the effect below). Nothing range-dependent fetches before
  // that — otherwise the first fetch goes out with no range, a second one
  // right after with the real range, and whichever lands last wins.
  const [rangeReady, setRangeReady] = useState(false);
  // Which range the current `dashboard` was fetched for, so screens can tell
  // "data for this range" apart from "leftover data from the previous range"
  // and show a skeleton instead of a wrong amount.
  const [loadedRangeKey, setLoadedRangeKey] = useState(null);
  const fetchSeq = useRef(0);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  // The theme choice saved on the account wins over this device's last one,
  // so picking "Oscuro" on one phone carries over to the others.
  useEffect(() => {
    if (profile?.theme_preference) setThemePreference(profile.theme_preference);
  }, [profile?.theme_preference, setThemePreference]);
  // Categorías has no nav entry — it's opened from Transacciones or the
  // Dashboard's Presupuestos, and its back arrow returns to whichever.
  const [categoriesBackTo, setCategoriesBackTo] = useState('transactions');
  const openGoal = useCallback((id) => {
    setOpenGoalId(id);
    setCurrentPage('goals');
  }, []);
  // Nav clicks on Metas always land on the list, not a previously open goal.
  const navigate = useCallback((page) => {
    if (page === 'goals') setOpenGoalId(null);
    setCurrentPage(page);
  }, []);
  const openCategories = useCallback((from) => {
    setCategoriesBackTo(from);
    setCurrentPage('categories');
  }, []);

  const handleLogout = useCallback(() => {
    api.removeToken();
    setUser(null);
    setProfile(null);
    setDashboard(null);
    setCategories([]);
    setTransactions([]);
    setGoals([]);
    setCurrentPage('dashboard');
  }, []);

  // Listen for auth:logout events (from 401 interceptor)
  useEffect(() => {
    const onLogout = () => handleLogout();
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, [handleLogout]);

  // Capture ?utm_source/medium/campaign/content as soon as the app loads —
  // first touch wins, read back once at registration (see lib/attribution).
  useEffect(() => {
    captureUtmParams();
    captureRefParam();
  }, []);

  // Notification clicks: the service worker postMessages an open tab to switch
  // section in-app; clean the ?page= param used when the app was launched fresh.
  useEffect(() => {
    const VALID = ['dashboard', 'transactions', 'categories', 'goals', 'charts', 'investments', 'chat', 'settings'];
    let cleanup;
    if ('serviceWorker' in navigator) {
      const onMsg = (e) => {
        if (e.data?.type === 'navigate' && VALID.includes(e.data.page)) {
          if (e.data.page === 'goals') setOpenGoalId(e.data.goal || null);
          setCurrentPage(e.data.page);
          // The tab may have been open for hours — whatever the push is
          // about (e.g. someone just joined a goal) isn't in memory yet.
          refreshRef.current();
        }
      };
      navigator.serviceWorker.addEventListener('message', onMsg);
      cleanup = () => navigator.serviceWorker.removeEventListener('message', onMsg);
    }
    if (new URLSearchParams(window.location.search).get('page')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    return cleanup;
  }, []);

  // Check for stored token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const userData = await api.getMe();
        setUser(userData);
        const profileData = await api.getProfile();
        setProfile(profileData);
        // Every load: restore this phone's push if it can, and report its
        // real state (Inicio banner + /admin). See push.js.
        syncPushOnLoad().then(setPushState);
      } catch (e) {
        console.error('Auth check failed:', e);
        api.removeToken();
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Proactive trial-urgency popup — reuses the existing paywall modal (perks,
  // upgrade CTA, referral alternative) but triggers on a schedule instead of
  // a blocked feature. Added 2026-09-17: the first real cohort from the TikTok
  // wave (~120 signups) starts hitting trial end this week, and the only
  // conversion nudge that can't get lost to a spam filter or a denied push
  // permission is something shown right in the app. Fires at most once per
  // calendar day (localStorage-deduped) — never nags every navigation — and
  // stops entirely once trial_urgency_shown_count hits the server-side cap
  // (TRIAL_URGENCY_MAX_SHOWS, added 2026-09-19 per explicit request: cycle it
  // hard early, but don't punish someone who's just going to stay on the free
  // plan and keep using the app for free).
  useEffect(() => {
    if (!profile?.onboarding_complete) return;
    (async () => {
      try {
        const billing = await api.getBillingStatus();
        if (billing.plan === 'pro') return; // already paying — nothing to push
        if (billing.trial_urgency_shown_count >= billing.trial_urgency_max_shows) return; // hit the cap, leave them alone
        const today = new Date().toISOString().slice(0, 10);
        const SHOWN_KEY = 'mipisto_trial_urgency_shown';
        let shownDate = null;
        try { shownDate = localStorage.getItem(SHOWN_KEY); } catch (_) { /* private mode etc. */ }
        if (shownDate === today) return;

        let message = null;
        if (billing.in_trial && billing.days_left <= 2) {
          message = billing.days_left <= 0
            ? 'Tu prueba de MiPisto Pro se acaba hoy — no pierdas la detección automática por Gmail, el registro por voz ni tu Coach financiero.'
            : `Te quedan ${billing.days_left} día(s) de tu prueba de MiPisto Pro. No pierdas la detección automática, el registro por voz ni tu Coach.`;
        } else if (!billing.has_access && billing.trial_ends_at) {
          message = 'Tu prueba de MiPisto Pro ya terminó. Reactivala para seguir con la detección automática, el registro por voz y tu Coach.';
        }

        if (message) {
          try { localStorage.setItem(SHOWN_KEY, today); } catch (_) { /* private mode etc. */ }
          api.recordUrgencyShown().catch(() => {}); // best effort — counted at display time, not dismissal
          // mustAcknowledge: user explicitly wants this one impossible to
          // swipe away by accident — closing requires actually upgrading or
          // clicking "Entiendo, seguir sin Pro" (both recorded server-side).
          openPaywall(message, { mustAcknowledge: true });
        }
      } catch (_) { /* best effort — never block the app over this */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.onboarding_complete]);

  const fetchAll = useCallback(async () => {
    if (!rangeReady) return;
    // Only the newest call may write state — a slower, older response must
    // never overwrite fresher data.
    const seq = ++fetchSeq.current;
    try {
      const params = {};
      if (dateRange?.from) params.start_date = dateRange.from.toISOString();
      if (dateRange?.to) {
        const toDate = new Date(dateRange.to);
        toDate.setHours(23, 59, 59, 999);
        params.end_date = toDate.toISOString();
      }

      // Goals and integrations load alongside the main data (they used to
      // wait for it), each failing on its own without blocking the rest.
      const [main, goalsRes, integRes] = await Promise.allSettled([
        Promise.all([
          api.getDashboard(params),
          api.getCategories(params),
          api.getTransactions({ ...params, limit: 100 })
        ]),
        api.getGoals(),
        api.getIntegrations(),
      ]);
      if (goalsRes.status === 'fulfilled') setGoals(goalsRes.value);
      else console.error('Error fetching goals:', goalsRes.reason);
      if (integRes.status === 'fulfilled') {
        const integ = integRes.value;
        setGmailOAuthEnabled(integ.google_oauth_enabled);
        setGmailOAuthVerified(integ.google_oauth_verified);
        setGmailAccessRequestedAt(integ.gmail_access_requested_at);
        setGmailIntegration((integ.integrations || []).find(i => i.provider === 'gmail') || null);
      }
      setGmailLoaded(true);
      if (main.status === 'rejected') throw main.reason;
      if (seq !== fetchSeq.current) return;
      const [dash, cats, txns] = main.value;
      setDashboard(dash);
      setCategories(cats);
      setTransactions(txns);
      setLoadedRangeKey(dateRangeKey(dateRange));
    } catch (e) {
      console.error('Error fetching data:', e);
    }
  }, [dateRange, rangeReady]);

  // Optimistic list edits (see LocalTransactionsContext). Applied to both the
  // Movimientos list and Inicio's recent movements; the refetch each caller
  // fires after its API call settles replaces them with the server's data.
  const localTransactions = useMemo(() => {
    const editList = (fn) => {
      setTransactions(list => fn(list || []));
      setDashboard(d => (d?.recent_transactions ? { ...d, recent_transactions: fn(d.recent_transactions) } : d));
    };
    const upsertIn = (list, txn) => (list.some(t => t.id === txn.id)
      ? list.map(t => (t.id === txn.id ? { ...t, ...txn } : t))
      : [txn, ...list]);
    return {
      upsert: (txn) => editList(list => upsertIn(list, txn)),
      remove: (id) => editList(list => list.filter(t => t.id !== id)),
      replace: (oldId, txn) => editList(list => upsertIn(list.filter(t => t.id !== oldId), txn)),
    };
  }, []);

  // Default the global date filter to the account's budget period once, on load.
  useEffect(() => {
    if (!rangeReady && profile?.onboarding_complete) {
      setRangeReady(true);
      setDateRange(budgetPeriodRange(profile.budget_period || 'monthly'));
    }
  }, [profile?.onboarding_complete, profile?.budget_period, rangeReady]);

  useEffect(() => {
    if (profile?.onboarding_complete) {
      fetchAll();
    }
  }, [profile?.onboarding_complete, fetchAll]);

  useEffect(() => {
    refreshRef.current = profile?.onboarding_complete
      ? () => { fetchAll(); setRefreshNonce(n => n + 1); }
      : () => {};
  }, [profile?.onboarding_complete, fetchAll]);

  // Coming back to the app after a while (switching apps on a phone, another
  // tab) refreshes everything — shared goals change without this device doing
  // anything. Short blips (< 30s) don't refetch.
  useEffect(() => {
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt > 30000) {
        hiddenAt = null;
        refreshRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const handleLogin = async (userData) => {
    setUser(userData);
    try {
      const profileData = await api.getProfile();
      setProfile(profileData);
    } catch (e) {
      console.error('Error fetching profile after login:', e);
    }
  };

  const handleOnboardingComplete = async (data) => {
    try {
      const result = await api.createProfile(data);
      setProfile(result);
    } catch (e) {
      // Previously silent (console.error only) — a handful of real users
      // were found stuck with an account but no profile (login_count: 1,
      // zero categories/transactions), almost certainly this failing with
      // no feedback and no way to retry. Re-thrown so Onboarding's submit
      // button can stop spinning and let them try again.
      console.error('Error creating profile:', e);
      toast.error(friendlyError(e, 'No se pudo guardar tu perfil. Intenta de nuevo.'));
      throw e;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  // Not logged in → show login page
  if (!user) {
    return (
      <>
        <Login onLogin={handleLogin} />
        <Toaster position="top-right" />
      </>
    );
  }

  // Logged in but no profile → show onboarding
  if (!profile?.onboarding_complete) {
    return (
      <>
        <Onboarding onComplete={handleOnboardingComplete} />
        <Toaster position="top-right" />
      </>
    );
  }

  // Existing accounts (created before acquisition-source tracking existed)
  // answer a one-time mandatory question here. New users answer the same
  // question inline during onboarding instead, so they never see this.
  const needsAcquisitionSource = profile?.onboarding_complete && !profile?.acquisition_source;

  // One-time feedback prompt — waits until the acquisition-source modal (if
  // any) and the dashboard tour are both out of the way, so a brand-new
  // account never gets three overlays stacked on their first load, *and*
  // until they've had a few days to actually use the app (see
  // FEEDBACK_MIN_DAYS_SINCE_ONBOARDING — asking someone to rate MiPisto
  // seconds after onboarding, before they've tried it, gets meaningless
  // answers). Gated on both feedback_* fields being unset so a submit *or* a
  // "Ahora no, gracias" dismiss both permanently clear it (see FeedbackModal.js).
  const daysSinceOnboarding = profile?.created_at
    ? (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
    : 0;
  const needsFeedback = profile?.onboarding_complete && !needsAcquisitionSource && profile?.has_seen_tour &&
    daysSinceOnboarding >= FEEDBACK_MIN_DAYS_SINCE_ONBOARDING &&
    !profile?.feedback_submitted_at && !profile?.feedback_dismissed_at;

  // "Your Gmail access request was approved" popup — see
  // GmailAccessGrantedModal.js and server.py's run_gmail_access_notifications
  // (same event also goes out by push/email; whichever reaches the user
  // first stamps gmail_access_notified_at so this doesn't also show once
  // they've already been told). Waits on gmailLoaded so this doesn't flash
  // based on the default (unverified) state before the real /integrations
  // fetch resolves. Takes priority over the feedback prompt — approved
  // access is actionable news, not a passive ask.
  const needsGmailAccessGranted = profile?.onboarding_complete && !needsAcquisitionSource && gmailLoaded &&
    !!gmailAccessRequestedAt && !profile?.gmail_access_notified_at && gmailOAuthVerified && !gmailIntegration;

  return (
    <LocalTransactionsContext.Provider value={localTransactions}>
    <ProfileMenuContext.Provider value={{ openProfileMenu: () => setProfileMenuOpen(true), photo: profile?.photo }}>
    <div className="app-layout" data-testid="app-layout">
      {needsAcquisitionSource ? (
        <AcquisitionSourceModal onSaved={(p) => setProfile(p)} />
      ) : (profile?.onboarding_complete && pendingGoalInvite) ? (
        <GoalInviteModal
          code={pendingGoalInvite}
          onDone={(goalId) => {
            clearPendingGoalInvite();
            setPendingGoalInvite(null);
            if (goalId) { fetchAll(); openGoal(goalId); }
          }}
        />
      ) : needsGmailAccessGranted ? (
        <GmailAccessGrantedModal onDone={() => api.getProfile().then(setProfile).catch(() => {})} />
      ) : needsFeedback ? (
        <FeedbackModal onDone={() => api.getProfile().then(setProfile).catch(() => {})} />
      ) : null}
      <GoogleConsentNotice />
      <Sidebar currentPage={currentPage} onNavigate={navigate} onLogout={handleLogout} userName={user?.name} userPhoto={profile?.photo} categories={categories} goals={goals} onRefresh={fetchAll} />
      <MobileNav currentPage={currentPage} onNavigate={navigate} />
      {/* Solid strip behind the notch / status bar on phones. With the
          translucent iOS status bar nothing covered it, so content scrolled
          visibly behind the clock and above each page's sticky header
          (which sticks at the safe-area inset). Zero height elsewhere. */}
      <div aria-hidden="true" className="md:hidden fixed inset-x-0 top-0 z-30 bg-surface" style={{ height: 'env(safe-area-inset-top)' }} />
      <main className="main-content">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {currentPage === 'dashboard' && (
            <Dashboard dashboard={dashboard} dataLoading={!dashboard || loadedRangeKey !== dateRangeKey(dateRange)} categories={categories} goals={goals} tourBlocked={!!pendingGoalInvite} onRefresh={fetchAll} profile={profile} onProfileUpdate={(p) => setProfile(p)} dateRange={dateRange} setDateRange={setDateRange} onNavigate={(page) => page === 'categories' ? openCategories('dashboard') : setCurrentPage(page)} gmailIntegration={gmailIntegration} gmailOAuthEnabled={gmailOAuthEnabled} gmailOAuthVerified={gmailOAuthVerified} gmailAccessRequestedAt={gmailAccessRequestedAt} gmailLoaded={gmailLoaded} pushState={pushState} />
          )}
          {currentPage === 'categories' && (
            <Categories categories={categories} onRefresh={fetchAll} dateRange={dateRange} setDateRange={setDateRange} budgetPeriod={profile?.budget_period} onBack={() => setCurrentPage(categoriesBackTo)} />
          )}
          {currentPage === 'transactions' && (
            <Transactions transactions={transactions} dataLoading={!dashboard || loadedRangeKey !== dateRangeKey(dateRange)} categories={categories} goals={goals} onRefresh={fetchAll} dateRange={dateRange} setDateRange={setDateRange} budgetPeriod={profile?.budget_period} onOpenCategories={() => openCategories('transactions')} />
          )}
          {currentPage === 'goals' && (
            <Goals goals={goals} onRefresh={fetchAll} openGoalId={openGoalId} setOpenGoalId={setOpenGoalId} refreshKey={refreshNonce} />
          )}
          {currentPage === 'charts' && (
            <Charts rangeReady={rangeReady} budgetCategories={categories} dateRange={dateRange} setDateRange={setDateRange} budgetPeriod={profile?.budget_period} />
          )}
          {currentPage === 'settings' && (
            <Settings user={user} profile={profile} onLogout={handleLogout} onProfileUpdate={(p) => { setProfile(p); setUser({ ...user, name: p.name }); }} />
          )}
          {currentPage === 'investments' && (
            <Investments />
          )}
          {currentPage === 'chat' && (
            <Suspense fallback={null}>
              <Chat profile={profile} onRefresh={fetchAll} />
            </Suspense>
          )}
        </div>
      </main>
      <ProfileMenu
        open={profileMenuOpen}
        onOpenChange={setProfileMenuOpen}
        onNavigate={navigate}
        onLogout={handleLogout}
        userName={profile?.name || user?.name}
        userPhoto={profile?.photo}
      />
      <Toaster position="top-right" />
    </div>
    </ProfileMenuContext.Provider>
    </LocalTransactionsContext.Provider>
  );
}

export default function App() {
  // Hidden admin route — checked before anything else so it's a completely
  // separate component tree from the consumer AppShell above (own auth gate,
  // own state, can't interfere with regular app rendering). Not linked from
  // any nav; see AdminApp.js for its own access check against ADMIN_EMAILS.
  if (window.location.pathname.startsWith('/admin')) {
    return <AdminApp />;
  }
  return (
    <AmountsVisibilityProvider>
      <PaywallProvider>
        <AppShell />
        <PaywallModal />
      </PaywallProvider>
    </AmountsVisibilityProvider>
  );
}
